/**
 * PREC-3616 — decides whether a tracker company is still waiting for its *first* signal scan
 * ("Looking for signals") or should fall back to today's behaviour ("No signal").
 *
 * Deliberately pure and dependency-free: the 24h cap is re-evaluated on every render, so a tab
 * left open past the boundary stops spinning by itself instead of waiting for a refetch.
 *
 * The two guards are load-bearing, not niceties. `signalLastCheckedAt` is written *only* at the
 * end of a successful scan, so it stays null forever when the tracker's list has no signal
 * config, the priority bucket has no interval, the company ran out of credits, or the scan threw.
 * Without `signalsConfigured` (the common case) and without the time cap (everything else) those
 * rows would spin forever.
 */

export type SignalScanState = "pending" | "checked";

/** How long after a company is added we keep showing "Looking for signals". */
export const FIRST_SCAN_PENDING_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface ResolveSignalScanStateInput {
	/** Does the tracker's list have a saved signal config? Without one no scan will ever run. */
	signalsConfigured: boolean;
	/** Null/undefined until the row's first successful scan. */
	signalLastCheckedAt?: Date | string | null;
	/** When the company was added to the tracker. */
	createdAt?: Date | string | null;
	/** Injectable clock, for tests. */
	now?: Date;
}

const toTimestamp = (value?: Date | string | null): number | null => {
	if (value === null || value === undefined) {
		return null;
	}

	const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();

	return Number.isNaN(timestamp) ? null : timestamp;
};

/**
 * Returns `"pending"` only when all three hold: signals are configured for the tracker's list, the
 * row has never been scanned, and it was added less than 24h ago. Every other case — including an
 * unparseable or missing `createdAt` — returns `"checked"`, so any uncertainty degrades to the
 * behaviour we shipped before this feature.
 */
export const resolveSignalScanState = ({
	signalsConfigured,
	signalLastCheckedAt,
	createdAt,
	now = new Date(),
}: ResolveSignalScanStateInput): SignalScanState => {
	if (!signalsConfigured) {
		return "checked";
	}

	if (toTimestamp(signalLastCheckedAt) !== null) {
		return "checked";
	}

	const createdAtTimestamp = toTimestamp(createdAt);

	if (createdAtTimestamp === null) {
		return "checked";
	}

	const age = now.getTime() - createdAtTimestamp;

	return age < FIRST_SCAN_PENDING_WINDOW_MS ? "pending" : "checked";
};

/**
 * The Signal cell's three states when a company has no signal to show (PREC-3240):
 * - `pending`: "Looking for signals" — the first scan is due (above), or the user just asked for one;
 * - `notScanned`: "Not scanned yet", with a "Run signal search" button;
 * - `scanned`: "No relevant signals found · checked <date>", with "Run again" on hover.
 *
 * Before this, every company older than a day read "No signal", whether it had been scanned and
 * found nothing or never been scanned at all.
 */
export type SignalCellState = "pending" | "notScanned" | "scanned";

/** How long a "Run signal search" keeps the cell on "Looking for signals" without an answer. */
export const REQUESTED_SCAN_PENDING_WINDOW_MS = 30 * 60 * 1000;

export interface ResolveSignalCellStateInput extends ResolveSignalScanStateInput {
	/** When the user asked for a scan of this company in this tab, if they did. */
	scanRequestedAt?: Date | null;
}

const isRequestedScanPending = (
	scanRequestedAt: Date | null | undefined,
	signalLastCheckedAt: Date | string | null | undefined,
	now: Date
): boolean => {
	const requested = toTimestamp(scanRequestedAt);

	if (requested === null || now.getTime() - requested >= REQUESTED_SCAN_PENDING_WINDOW_MS) {
		return false;
	}

	const checked = toTimestamp(signalLastCheckedAt);

	return checked === null || checked < requested;
};

export const resolveSignalCellState = (input: ResolveSignalCellStateInput): SignalCellState => {
	const now = input.now ?? new Date();

	if (isRequestedScanPending(input.scanRequestedAt, input.signalLastCheckedAt, now)) {
		return "pending";
	}

	if (resolveSignalScanState({ ...input, now }) === "pending") {
		return "pending";
	}

	return toTimestamp(input.signalLastCheckedAt) === null ? "notScanned" : "scanned";
};
